import { hasAuthParams, toOrigin } from '../utils/authUtils';
import { User as OidcUser, UserManager, WebStorageStateStore } from 'oidc-client-ts';

// General api for auth.
export type AuthApi = {
  getAccessToken: () => Promise<string>;
  getUser: () => Promise<OidcUser | null>;
  clearUser: () => Promise<void>;
  getFlowId: () => string | null;
};

export type AuthConfig = {
  redirectUri: string;
  clientId: string;
  domain: string;
};

type AppState = { returnTo?: string };

// This sdk is introduced to handle authentication flows before the react app is loaded.
// Due to the reactive nature of the react app, multiple authentication flows in one app are difficult to handle.
// the auth context is injected in the window object, so it can be used in the app.
const STATE_STORE_PREFIX = 'fa';

export const initAuth = async (authConfig: AuthConfig): Promise<void> => {
  const userManager = new UserManager({
    client_id: authConfig.clientId,
    authority: toOrigin(authConfig.domain),
    redirect_uri: '',
    stateStore: new WebStorageStateStore({
      store: window.localStorage,
      prefix: STATE_STORE_PREFIX,
    }),
    scope: 'openid profile email offline_access update:identity',
  });

  const clearAuthParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('code');
    searchParams.delete('error');
    window.history.replaceState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
  };

  // Other screens carry their own code/error params; only a state this app stored is its callback.
  const isOwnCallback = () => {
    const state = new URLSearchParams(window.location.search).get('state');
    return !!state && window.localStorage.getItem(STATE_STORE_PREFIX + state) !== null;
  };

  const handleAuthCallback = async () => {
    const user = await userManager.signinCallback();
    if (user && user.state && (user.state as AppState).returnTo) {
      window.history.replaceState({}, '', (user.state as AppState).returnTo);
    }
  };

  const injectAuthInApp = () => {
    window.auth = {
      getAccessToken: async () => {
        const user = await userManager.getUser();
        if (!user) {
          await doAuth(userManager);
          return '';
        }
        return user.access_token;
      },
      getUser: () => userManager.getUser(),
      clearUser: async () => {
        await userManager.clearStaleState();
        await userManager.removeUser();
      },
      getFlowId: () => {
        const params = new URLSearchParams(window.location.search);
        return params.get('flowId');
      },
    };
  };

  const doAuth = async (manager: UserManager) => {
    // Add the appstate to the auth flow, so it can be used in the callback to return to the original uri.
    const appState: AppState = {
      returnTo: window.location.pathname + window.location.search,
    };
    // The fixed redirect_uri below drops ui_locales, so the login page needs it on the request.
    const uiLocales = new URLSearchParams(window.location.search).get('ui_locales');

    await manager.signinRedirect({
      state: appState,
      ...(uiLocales ? { extraQueryParams: { ui_locales: uiLocales } } : {}),
      // FusionAuth requires strict redirect_uri matching, so it carries no params. The original URL
      // lives in appState.returnTo and is restored after the callback.
      redirect_uri: authConfig.redirectUri,
    });
  };

  // Main flow starts here
  // If the auth params are present in the url, handle the callback.
  if (hasAuthParams()) {
    if (isOwnCallback()) {
      await handleAuthCallback();
      clearAuthParams();
    }
  } else {
    await userManager.clearStaleState();
    // Start the effective auth flow.
    await doAuth(userManager);
  }

  // Make the auth api available in the app. (registered on window.auth)
  injectAuthInApp();
};
